import React from 'react';

import { UserTagResponse } from 'ui/types';

import API from 'utils/api';

import { CommentText, MentionLink, MentionText, UrlLink } from './styles';
import { CommentProps } from './types';
import Item from '../Item';
import { useSearch } from '../../../utils/hooks/search';
import AddCommentActivities from '../../AddCommentActivities';

const emailRegExp = /@[a-zA-Z0-9+._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+/gi;
const emailSplittingRegExp = /(@[a-zA-Z0-9+._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)|([^@]+)/gi;
const urlRegExp = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi;

const Comment: React.FunctionComponent<CommentProps> = ({
  activity,
  onDeleteComment,
  onUpdateComment,
  userTagBaseUrl,
  sessionUser,
  nonEditable,
}) => {
  const [editing, setEditing] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [loadUsers, setLoadUsers] = React.useState(false);
  const { results: userList } = useSearch(API.user.searchUsers, loadUsers);
  const [fragments, setFragments] = React.useState<React.ReactNode>([]);

  const handleClose = (): void => {
    setEditing(false);
  };

  const onEdit = (): void => {
    setImmediate(() => setEditing(true));
  };

  const onSendMessage = async (message: string): Promise<void> => {
    handleClose();
    setSubmitting(true);
    onUpdateComment && (await onUpdateComment(activity.uuid, message));
    setSubmitting(false);
  };

  const isCurrentUserAuthor = activity?.creator?.uuid === sessionUser.uuid;

  const linkifyText = (text: string, keyPrefix: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    const regex = new RegExp(urlRegExp.source, 'gi');
    const matches = text.matchAll(regex);

    for (const match of matches) {
      if (match.index !== undefined && match.index > lastIndex) {
        result.push(text.slice(lastIndex, match.index));
      }
      const url = match[0];
      result.push(
        <UrlLink
          key={`${keyPrefix}-url-${match.index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          isAuthor={isCurrentUserAuthor}
        >
          {url}
        </UrlLink>
      );
      lastIndex = (match.index ?? 0) + url.length;
    }

    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }

    return result.length > 0 ? result : [text];
  };

  React.useEffect(() => {
    const setFragmentsFromEmailRegex = (): void => {
      let remainingText = activity.message;

      const newFragments = [];
      const users = activity.message.match(emailRegExp) || [];

      const addText = (text: string): void => {
        let remaining = text;
        while (remaining.indexOf('\n') !== -1) {
          const pos = remaining.indexOf('\n');
          if (pos > 0) {
            newFragments.push(...linkifyText(remaining.slice(0, pos), `email-${newFragments.length}`));
          }
          newFragments.push(<br key={`br-${newFragments.length}`} />);

          remaining = remaining.slice(pos + 1);
        }
        newFragments.push(...linkifyText(remaining, `email-${newFragments.length}`));
      };

      for (const user of users) {
        if (!loadUsers) {
          setLoadUsers(true);
        }
        const starts = remainingText.indexOf(user);
        addText(remainingText.slice(0, starts));
        newFragments.push(
          <MentionText isAuthor={isCurrentUserAuthor} key={newFragments.length}>
            @{(userList.find((userItem) => user.trim() === `@${userItem.email}`) || { name: user.slice(1) }).name}
          </MentionText>
        );

        remainingText = remainingText.slice(starts + user.length);
      }
      if (remainingText.length > 0) {
        addText(remainingText);
      }
      setFragments(newFragments);
    };

    const setFragmentsFromTaggedUsers = (taggedUsers: UserTagResponse[]): void => {
      const newFragments = (activity.message.split(emailSplittingRegExp).filter(Boolean) || []).flatMap(
        (word: string, index: number) => {
          const found = taggedUsers.find((tag) => word.includes(tag.name) || word.includes(tag.email));
          if (word.startsWith('@') && found) {
            if (found.isClickable && userTagBaseUrl) {
              return (
                <MentionLink
                  key={`mention-${index}`}
                  isAuthor={isCurrentUserAuthor}
                  href={`${userTagBaseUrl}${found?.uuid}/`}
                >{`@${found?.name}`}</MentionLink>
              );
            }
            return (
              <MentionText key={`mention-${index}`} isAuthor={isCurrentUserAuthor}>{`@${found?.name}`}</MentionText>
            );
          }
          if (word === '\n') {
            return <br key={`br-${index}`} />;
          }
          return linkifyText(word, `tagged-${index}`);
        }
      );
      setFragments(newFragments);
    };

    if (activity.taggedUsers) {
      setFragmentsFromTaggedUsers(activity.taggedUsers);
    } else {
      setFragmentsFromEmailRegex();
    }
  }, [activity.message, activity.taggedUsers, isCurrentUserAuthor, loadUsers, userList, userTagBaseUrl]);

  return (
    <Item
      activity={activity}
      onEditItem={onEdit}
      onDeleteItem={onDeleteComment}
      sessionUser={sessionUser}
      nonEditable={nonEditable}
    >
      {editing ? (
        <AddCommentActivities
          sendMessage={onSendMessage}
          onBlur={() => setEditing(false)}
          onEscape={() => setEditing(false)}
          value={activity.message}
        />
      ) : (
        <CommentText
          isAuthor={isCurrentUserAuthor}
          id={`comment-created-by-${activity?.creator?.uuid === sessionUser.uuid ? 'logged-in-user' : 'other-user'}`}
        >
          {submitting ? 'Updating comment...' : fragments}
        </CommentText>
      )}
    </Item>
  );
};

export default Comment;
